import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  resolveMediaUrl,
  isImageMime,
  isLikelyImageUrl,
  isLikelyPdfUrl
} from "../../utils/fileUploadHelpers";
import "./FileUploadWithPreview.css";

function isPdfFile(file) {
  if (!file) return false;
  return file.type === "application/pdf" || /\.pdf$/i.test(file.name || "");
}

/**
 * Reusable file input: local preview on select, optional existing URL when editing.
 * Revokes blob URLs on change/unmount.
 *
 * Props: id, label, file, onFileChange, existingUrl?, accept?, disabled?, required?, className?, hint?
 */
const FileUploadWithPreview = ({
  id,
  label,
  file,
  onFileChange,
  existingUrl = null,
  accept = ".jpeg,.jpg,.png,.gif,.webp,.pdf",
  disabled = false,
  required = false,
  className = "",
  hint = null
}) => {
  const inputRef = useRef(null);
  const [imgBroken, setImgBroken] = useState(false);

  const objectUrl = useMemo(() => {
    if (!file) return null;
    return URL.createObjectURL(file);
  }, [file]);

  useEffect(() => {
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [objectUrl]);

  useEffect(() => {
    setImgBroken(false);
  }, [file, existingUrl]);

  const resolvedExisting =
    existingUrl && String(existingUrl).trim() ? resolveMediaUrl(existingUrl) : "";

  const newIsImage = Boolean(file && isImageMime(file.type));
  const newIsPdf = Boolean(file && isPdfFile(file));
  const legacyIsImage = Boolean(!file && resolvedExisting && isLikelyImageUrl(existingUrl));
  const legacyIsPdf = Boolean(!file && resolvedExisting && isLikelyPdfUrl(existingUrl));

  const tryImageSrc = newIsImage
    ? objectUrl
    : legacyIsImage && !imgBroken
      ? resolvedExisting
      : null;

  const pdfOpenHref = newIsPdf ? objectUrl : legacyIsPdf ? resolvedExisting : null;

  const clearSelection = () => {
    if (inputRef.current) inputRef.current.value = "";
    onFileChange(null);
  };

  const hasNew = Boolean(file);
  const hasLegacy = Boolean(resolvedExisting);

  let preview = null;
  if (tryImageSrc) {
    preview = (
      <div className="fup-preview-wrap">
        <img
          src={tryImageSrc}
          alt=""
          className="fup-preview-img"
          onError={() => setImgBroken(true)}
        />
      </div>
    );
  } else if (pdfOpenHref) {
    preview = (
      <div className="fup-preview-wrap">
        <div className="fup-preview-pdf">
          <i className="fas fa-file-pdf" aria-hidden />
          <span>{file ? file.name : "Current document (PDF)"}</span>
          <a href={pdfOpenHref} target="_blank" rel="noopener noreferrer">
            Open / download
          </a>
        </div>
      </div>
    );
  } else if (hasNew && file) {
    preview = (
      <div className="fup-preview-wrap">
        <div className="fup-preview-file">
          <i className="fas fa-file" aria-hidden />
          <span>{file.name}</span>
        </div>
      </div>
    );
  } else if (hasLegacy && resolvedExisting) {
    preview = (
      <div className="fup-preview-wrap">
        <div className="fup-preview-file">
          <i className="fas fa-paperclip" aria-hidden />
          <a href={resolvedExisting} target="_blank" rel="noopener noreferrer">
            View current file
          </a>
        </div>
      </div>
    );
  } else {
    preview = (
      <div className="fup-preview-wrap">
        <div className="fup-placeholder">No file selected</div>
      </div>
    );
  }

  return (
    <div className={`fup ${className}`.trim()}>
      <span className="fup-label">
        {label}
        {required ? <span className="req"> *</span> : null}
      </span>

      {preview}

      <div className="fup-actions">
        <input
          ref={inputRef}
          id={id}
          type="file"
          accept={accept}
          disabled={disabled}
          aria-label={typeof label === "string" ? label : "Upload file"}
          onChange={(e) => {
            const f = e.target.files?.[0] || null;
            onFileChange(f);
          }}
        />
        {hasNew ? (
          <button
            type="button"
            className="fup-clear"
            disabled={disabled}
            onClick={clearSelection}
          >
            Remove new file
          </button>
        ) : null}
      </div>

      {hint ? <p className="fup-hint">{hint}</p> : null}
    </div>
  );
};

export default FileUploadWithPreview;
